/**
 * Historial de Cajas — consulta y arqueo de un turno.
 *
 * DISEÑO
 *
 * Es una planilla de caja, no un tablero. El encargado entra a responder una
 * sola pregunta — "¿cuánto entró y cuadra?" — así que esa respuesta va arriba
 * y en grande, y el resto es respaldo ordenado.
 *
 * Reglas que sigue la pantalla:
 *
 *   1. El color SIGNIFICA algo. Verde y rojo aparecen únicamente en el
 *      resultado del arqueo. Las categorías (habitación, restaurante, Sego) no
 *      son niveles de alerta, así que no llevan color propio: se distinguen por
 *      posición y etiqueta. Antes cada sección inventaba su paleta —azul,
 *      naranja, morado, amarillo— y el ojo no sabía dónde mirar.
 *
 *   2. Los números se leen en columna. `tabular-nums` en todo lo monetario para
 *      que las cifras se alineen dígito con dígito.
 *
 *   3. Separadores antes que cajas. Líneas finas del tema en vez de tarjetas
 *      pastel con borde: menos ruido y la jerarquía la da el tamaño del texto.
 *
 *   4. Una sola vez cada cosa. El bloque "CONTEO FÍSICO REQUERIDO" con firmas
 *      que vivía acá quedó absorbido por el arqueo, que hace lo mismo pero de
 *      verdad. Las firmas se imprimen desde el acta.
 */

import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import { getApi } from 'views/services/api';
import CashAuditPanel from './CashAuditPanel';
import { SectionTitle, TurnBreakdown, TurnTransactions } from './CashTurnBreakdown';
import { dateTime, money, numeric } from './cashShared';

const CashRegisterReport = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [registerNumber, setRegisterNumber] = useState('');
  const [reportData, setReportData] = useState(null);
  // Cajas cerradas que todavía nadie arqueó. Es la portada de la pantalla: el
  // encargado ve el trabajo pendiente sin tener que saberse los números.
  const [pendingAudits, setPendingAudits] = useState([]);
  // El total puede ser mayor que las filas listadas: el servidor devuelve las
  // 100 más recientes. Con un backlog de cientos, mostrar el largo de la lista
  // haría creer que el problema es más chico de lo que es.
  const [pendingTotal, setPendingTotal] = useState(0);

  const fetchPendingAudits = async () => {
    try {
      const r = await getApi('api/cashregister/pending-audit');
      setPendingAudits(Array.isArray(r?.data?.items) ? r.data.items : []);
      setPendingTotal(Number(r?.data?.total) || 0);
    } catch (err) {
      console.error('Error al cargar cajas pendientes de arqueo:', err);
    }
  };

  useEffect(() => {
    fetchPendingAudits();
  }, []);

  /** Carga el reporte de una caja por su id, sin pasar por la búsqueda. */
  const loadReportById = async (cashRegisterId, number) => {
    setLoading(true);
    setError(null);
    try {
      const reportResponse = await getApi(`api/cashregister/report/${cashRegisterId}`);
      if (!reportResponse?.data) throw new Error('No se pudo obtener el reporte');
      setReportData(reportResponse.data);
      if (number !== undefined) setRegisterNumber(String(number));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    if (!registerNumber) {
      setError('Ingresá el número de caja que querés consultar.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const registerResponse = await getApi(`api/cashregister/closed/number/${registerNumber}`);
      if (!registerResponse?.data) throw new Error('Caja no encontrada');

      const cashRegister = registerResponse.data;
      const reportResponse = await getApi(`api/cashregister/report/${cashRegister._id}`);
      if (!reportResponse?.data) throw new Error('No se pudo obtener el reporte');

      setReportData(reportResponse.data);
    } catch (err) {
      console.error('Error al cargar reporte:', err);
      setError(err.response?.data?.error || err.message || 'Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const register = reportData?.cashRegister;
  const summary = reportData?.summary;
  const baseCurrency = register?.baseCurrency || 'USD';

  const auditChip = () => {
    if (!register) return null;
    if (register.auditStatus === 'audited') {
      const difference = Number(register.audit?.totalDifferenceBase || 0);
      return (
        <Chip
          size="small"
          color={Math.abs(difference) < 0.01 ? 'success' : 'error'}
          label={Math.abs(difference) < 0.01 ? 'Arqueada · cuadra' : `Arqueada · ${difference.toFixed(2)}`}
        />
      );
    }
    if (register.auditStatus === 'legacy') {
      return <Chip size="small" variant="outlined" label="Anterior al arqueo" />;
    }
    return <Chip size="small" color="warning" label="Pendiente de arqueo" />;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'grey.50', minHeight: '100%' }}>
      {/* La hoja impresa es el acta: fuera controles y fondos. */}
      <style>{`
        @media print {
          .print-hide { display: none !important; }
          body { background: #fff !important; }
        }
      `}</style>

      {/* ───────────────── Pendientes de arqueo ───────────────── */}
      {pendingTotal > 0 && (
        <Card variant="outlined" className="print-hide" sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.25 }}>
              <PendingActionsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="subtitle1" fontWeight={700}>
                Pendientes de arqueo
              </Typography>
              <Chip size="small" label={pendingTotal} sx={{ ...numeric, fontWeight: 700 }} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Turnos cerrados que todavía nadie cuadró contra el efectivo.
              {pendingTotal > pendingAudits.length && ` Se muestran los ${pendingAudits.length} más recientes.`}
            </Typography>

            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Caja</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Abrió</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Cerró</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Cierre</TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      Total registrado
                    </TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingAudits.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ ...numeric, fontWeight: 700 }}>#{row.registerNumber}</TableCell>
                      <TableCell>{row.openedBy || '—'}</TableCell>
                      <TableCell>{row.closedBy || '—'}</TableCell>
                      <TableCell sx={numeric}>{dateTime(row.closedAt)}</TableCell>
                      <TableCell align="right" sx={{ ...numeric, fontWeight: 600 }}>
                        {money(row.totalInBaseCurrency, row.baseCurrency)}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="text" onClick={() => loadReportById(row.id, row.registerNumber)}>
                          Arquear
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* ───────────────── Buscador ───────────────── */}
      <Card variant="outlined" className="print-hide" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Consultar una caja
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              label="Número de caja"
              type="number"
              value={registerNumber}
              onChange={(e) => setRegisterNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchReport()}
              variant="outlined"
              size="small"
              placeholder="Ej. 618"
              sx={{ maxWidth: { sm: 260 } }}
              fullWidth
            />
            <Button variant="contained" disableElevation startIcon={<SearchIcon />} onClick={fetchReport}>
              Buscar
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {loading && (
        <Box display="flex" alignItems="center" justifyContent="center" gap={2} py={6}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Cargando el turno…
          </Typography>
        </Box>
      )}

      {reportData && !loading && (
        <>
          {/* ───────────────── Encabezado del turno ───────────────── */}
          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: 2.5, md: 4 }}
                alignItems={{ md: 'flex-start' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em', fontWeight: 700 }}>
                    Turno de caja
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.25 }}>
                    <Typography variant="h3" fontWeight={700} sx={numeric}>
                      #{register.registerNumber}
                    </Typography>
                    {auditChip()}
                  </Stack>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 4 }} sx={{ flexGrow: 1 }}>
                  <Stack direction="row" spacing={1.25}>
                    <LoginIcon fontSize="small" sx={{ color: 'text.disabled', mt: 0.25 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Apertura
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={numeric}>
                        {dateTime(register.openedAt)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {register.openedBy?.name || '—'}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1.25}>
                    <LogoutIcon fontSize="small" sx={{ color: 'text.disabled', mt: 0.25 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Cierre
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={numeric}>
                        {dateTime(register.closedAt)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {register.closedBy?.name || '—'}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>

                <Button
                  className="print-hide"
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  sx={{ flexShrink: 0 }}
                >
                  Imprimir
                </Button>
              </Stack>

              <Divider sx={{ my: 2.5 }} />

              {/* La respuesta a la pregunta con la que entrás. */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                divider={<Divider orientation="vertical" flexItem />}
                spacing={{ xs: 2, sm: 0 }}
              >
                <Box sx={{ pr: { sm: 4 } }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Total del turno ({baseCurrency})
                  </Typography>
                  <Typography variant="h2" fontWeight={700} sx={{ ...numeric, lineHeight: 1.15 }}>
                    {money(summary.totalInBaseCurrency, baseCurrency)}
                  </Typography>
                </Box>

                {(register.openingFloat || []).length > 0 && (
                  <Box sx={{ px: { sm: 4 } }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Fondo de apertura
                    </Typography>
                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                      {register.openingFloat.map((row) => (
                        <Typography key={row.currency} variant="body1" fontWeight={600} sx={numeric}>
                          {money(row.amount, row.currency)}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}

                <Box sx={{ px: { sm: 4 } }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Transacciones
                  </Typography>
                  <Typography variant="h4" fontWeight={600} sx={{ ...numeric, lineHeight: 1.6 }}>
                    {summary.totalTransactions}
                  </Typography>
                </Box>

                {Object.keys(summary.totalsByCurrency || {}).length > 0 && (
                  <Box sx={{ px: { sm: 4 } }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Recibido en otras monedas
                    </Typography>
                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                      {Object.entries(summary.totalsByCurrency).map(([currency, amount]) => (
                        <Typography key={currency} variant="body1" fontWeight={600} sx={numeric}>
                          {money(amount, currency)}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* ───────────────── Desglose ───────────────── */}
          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <SectionTitle hint="A qué se imputó el dinero y por qué medio entró.">Desglose del turno</SectionTitle>
              <TurnBreakdown summary={summary} baseCurrency={baseCurrency} />
            </CardContent>
          </Card>

          {/* ───────────────── Detalle ───────────────── */}
          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <SectionTitle hint="Cada cobro que entró en el turno.">Movimientos</SectionTitle>
              <TurnTransactions transactions={reportData.transactions} baseCurrency={baseCurrency} />
            </CardContent>
          </Card>

          {/* ───────────────── Arqueo ─────────────────
              La única sección con color: es la única que tiene estado. */}
          {!register.isOpen && (
            <CashAuditPanel
              key={`${register.id}-${register.auditStatus}`}
              registerId={String(register.id)}
              onAudited={() => {
                fetchPendingAudits();
                loadReportById(register.id, register.registerNumber);
              }}
            />
          )}

          {reportData.isFrozen === false && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', textAlign: 'center', mt: 1, mb: 3 }}
            >
              Turno cerrado antes de que el sistema guardara el corte: las cifras se recalcularon ahora.
            </Typography>
          )}
        </>
      )}

      {!reportData && !loading && pendingTotal === 0 && (
        <Box
          sx={{
            py: 8,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: alpha(theme.palette.common.white, 0.6)
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No hay cajas pendientes de arqueo.
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Buscá por número para consultar un turno anterior.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CashRegisterReport;
